/**
 * Phase 1.9 — Add Contact dialog.
 *
 * Shared between ContactsPage (v1) and ContactsPageV2 (v2). Three-section
 * form per the Operations → Contacts spec:
 *
 *   1. Personal Information — first name, last name, email, phone
 *   2. Contact Information  — company (required), address
 *   3. Preferences          — tags (multi-select), notes
 *
 * Posts to /api/contacts and calls onAdded() on success so callers can
 * refetch their list. Tag options come from /api/docs/taxonomy/tags.
 *
 * `company` is the only field with hard validation. Everything else is
 * optional. We trim empties to null on submit so the worker never has
 * to second-guess "" vs null.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Plus, Mail, Phone, Building2, MapPin, FileText, Tags as TagsIcon, User } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { authFetch } from '@/lib/auth';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

interface TagOption {
  id: string;
  name: string;
  color: string | null;
}

export interface AddContactDialogProps {
  trigger?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onAdded?: () => void;
}

const DEFAULT_TRIGGER = (
  <Button
    size="sm"
    className="bg-burnt-orange text-white hover:bg-burnt-orange/90 dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90"
  >
    <Plus className="h-4 w-4" />
    Add contact
  </Button>
);

// Lightweight email shape check. We don't try to be RFC 5322-strict;
// the worker re-validates with z.string().email() and a stricter parser
// would just frustrate users with edge-case false negatives.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function AddContactDialog({
  trigger,
  open: controlledOpen,
  onOpenChange,
  onAdded,
}: AddContactDialogProps): JSX.Element {
  const [internalOpen, setInternalOpen] = useState<boolean>(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = (next: boolean): void => {
    setInternalOpen(next);
    onOpenChange?.(next);
  };

  // Form state mirrors D1 column names so the POST body can be sent
  // verbatim — no field renames in the network layer.
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [company, setCompany] = useState<string>('');
  const [address, setAddress] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [tagOptions, setTagOptions] = useState<TagOption[]>([]);
  const [submitting, setSubmitting] = useState<boolean>(false);

  // Pull tag options when the dialog opens so a closed dialog isn't
  // doing background fetches on every page mount.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    authFetch<{ items: TagOption[] }>('/api/docs/taxonomy/tags')
      .then((data) => {
        if (!cancelled) setTagOptions(data.items ?? []);
      })
      .catch(() => {
        // If tags can't load, fall back to "no tags available" UI; the
        // form is still usable without them.
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const reset = (): void => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setCompany('');
    setAddress('');
    setNotes('');
    setTagIds([]);
  };

  const toggleTag = (id: string): void => {
    setTagIds((prev) => (prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]));
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!company.trim()) {
      toast.error('Company is required');
      return;
    }
    const trimmedEmail = email.trim();
    if (trimmedEmail && !EMAIL_RE.test(trimmedEmail)) {
      toast.error('Email looks invalid');
      return;
    }
    setSubmitting(true);
    try {
      await authFetch('/api/contacts', {
        method: 'POST',
        body: JSON.stringify({
          company: company.trim(),
          first_name: firstName.trim() || null,
          last_name: lastName.trim() || null,
          email: trimmedEmail || null,
          phone: phone.trim() || null,
          address: address.trim() || null,
          notes: notes.trim() || null,
          tag_ids: tagIds,
        }),
      });
      toast.success('Contact added');
      reset();
      setOpen(false);
      onAdded?.();
    } catch (err) {
      toast.error((err as Error).message ?? 'Failed to add contact');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger ?? DEFAULT_TRIGGER}</DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Contact</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-5 mt-2">
          <Section title="Personal Information" icon={User}>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="contact-first-name">First Name</Label>
                <Input
                  id="contact-first-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Jane"
                  autoComplete="given-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-last-name">Last Name</Label>
                <Input
                  id="contact-last-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Smith"
                  autoComplete="family-name"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="contact-email" className="inline-flex items-center gap-1">
                  <Mail className="h-3.5 w-3.5" aria-hidden="true" />
                  Email
                </Label>
                <Input
                  id="contact-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="jane@example.com"
                  autoComplete="email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-phone" className="inline-flex items-center gap-1">
                  <Phone className="h-3.5 w-3.5" aria-hidden="true" />
                  Phone
                </Label>
                <Input
                  id="contact-phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="555-123-4567"
                  autoComplete="tel"
                />
              </div>
            </div>
          </Section>

          <Section title="Contact Information" icon={Building2}>
            <div className="space-y-2">
              <Label htmlFor="contact-company">
                Company <span className="text-clay dark:text-clay-dark">*</span>
              </Label>
              <Input
                id="contact-company"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                placeholder="Valley Vet Clinic"
                autoComplete="organization"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact-address" className="inline-flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
                Address
              </Label>
              <Textarea
                id="contact-address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                rows={2}
                placeholder="123 Main St, Anytown, USA"
                autoComplete="street-address"
              />
            </div>
          </Section>

          <Section title="Preferences" icon={TagsIcon}>
            <div className="space-y-2">
              <Label>Tags</Label>
              {tagOptions.length === 0 ? (
                <p className="text-2xs text-muted-foreground">
                  No tags defined. Manage tags under Documents → Taxonomy.
                </p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {tagOptions.map((tag) => {
                    const selected = tagIds.includes(tag.id);
                    return (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={() => toggleTag(tag.id)}
                        aria-pressed={selected}
                        className={cn(
                          'inline-flex items-center rounded-full border px-2.5 py-1 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-burnt-orange',
                          selected
                            ? 'border-forest bg-forest/10 text-forest dark:border-forest-dark dark:bg-forest-dark/20 dark:text-forest-dark'
                            : 'border-border bg-surface text-muted-foreground hover:bg-surface-raised dark:bg-surface-dark dark:hover:bg-surface-raised-dark',
                        )}
                      >
                        {selected ? <span className="mr-1 text-2xs">✓</span> : null}
                        {tag.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact-notes" className="inline-flex items-center gap-1">
                <FileText className="h-3.5 w-3.5" aria-hidden="true" />
                Notes
              </Label>
              <Textarea
                id="contact-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Anything worth remembering about this contact."
              />
            </div>
          </Section>

          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-burnt-orange hover:bg-burnt-orange/90 text-white dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90"
          >
            {submitting ? 'Adding...' : 'Add Contact'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface SectionProps {
  title: string;
  icon: React.ElementType;
  children: ReactNode;
}

function Section({ title, icon: Icon, children }: SectionProps): JSX.Element {
  return (
    <fieldset className="space-y-3 rounded-lg border border-border p-4 dark:border-border">
      <legend className="-ml-1 inline-flex items-center gap-1.5 px-1 font-mono text-2xs uppercase tracking-wider text-muted-foreground">
        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
        {title}
      </legend>
      {children}
    </fieldset>
  );
}
